import { PathLayer } from '@deck.gl/layers';

/**
 * Generates a Deck.gl PathLayer for vessel tracks.
 * 
 * @param {Array} data - Raw data array containing {lat, lon, timestamp, speed_knots, heading_deg}
 * @param {Object} options - Configuration options
 * @returns {PathLayer} - configured Deck.gl layer
 */
/**
 * Build segments from an array of per-vessel tracks (each track = array of points in time order).
 * Used so the blue path follows each vessel's track instead of mixing vessels when multiple are shown.
 */
export const buildTrackSegmentsFromTracks = (tracks) => {
    if (!tracks || !Array.isArray(tracks)) return [];
    const segments = [];
    for (const track of tracks) {
        const valid = (track || []).filter(d =>
            d && d.lat != null && d.lon != null && !isNaN(d.lat) && !isNaN(d.lon) && (d.timestamp || d.time)
        );
        for (let i = 0; i < valid.length - 1; i++) {
            const start = valid[i];
            const end = valid[i + 1];
            segments.push({
                path: [[start.lon, start.lat], [end.lon, end.lat]],
                startPoint: start,
                endPoint: end,
                speed: start.speed_knots ?? start.speed ?? 0
            });
        }
    }
    return segments;
};

export const generateTrackLayer = (data, options = {}) => {
    const {
        id = 'track-layer',
        visible = true,
        widthScale = 1,
        onHover,
        onClick
    } = options;

    if (!data || data.length === 0) return null;

    // Allow pre-built segments (e.g. from buildTrackSegmentsFromTracks) so path is per-vessel
    let segments = [];
    if (data[0] && Array.isArray(data[0].path) && data[0].startPoint != null) {
        segments = data;
    } else {
        // 1. Process Data: Filter, Sort, and Create Segments from raw points
        const validPoints = data
            .filter(d =>
                d.lat != null && d.lon != null &&
                !isNaN(d.lat) && !isNaN(d.lon) &&
                (d.timestamp || d.time) // ensure time for sorting
            )
            .sort((a, b) => new Date(a.timestamp || a.time) - new Date(b.timestamp || b.time));

        if (validPoints.length < 2) return null;

        for (let i = 0; i < validPoints.length - 1; i++) {
            const start = validPoints[i];
            const end = validPoints[i + 1];
            segments.push({
                path: [[start.lon, start.lat], [end.lon, end.lat]],
                startPoint: start,
                endPoint: end,
                speed: start.speed_knots || start.speed || 0
            });
        }
    }

    if (segments.length === 0) return null;

    // 2. Color Logic
    // Blue (< 2.5) -> Yellow (3.5) -> Red (> 4.5)
    const getColor = (d) => {
        const speed = d.speed;

        if (speed <= 2.5) {
            return [0, 0, 255]; // Blue
        } else if (speed >= 4.5) {
            return [255, 0, 0]; // Red
        } else {
            // Interpolate
            // Range 2.5 to 4.5. Midpoint 3.5 is Yellow [255, 255, 0]

            if (speed < 3.5) {
                // 2.5 (Blue) to 3.5 (Yellow)
                const t = (speed - 2.5) / 1.0;
                // Blue: [0, 0, 255] -> Yellow: [255, 255, 0]
                // R: 0 -> 255
                // G: 0 -> 255
                // B: 255 -> 0
                return [
                    Math.round(255 * t),
                    Math.round(255 * t),
                    Math.round(255 * (1 - t))
                ];
            } else {
                // 3.5 (Yellow) to 4.5 (Red)
                const t = (speed - 3.5) / 1.0;
                // Yellow: [255, 255, 0] -> Red: [255, 0, 0]
                // R: 255 -> 255
                // G: 255 -> 0
                // B: 0 -> 0
                return [
                    255,
                    Math.round(255 * (1 - t)),
                    0
                ];
            }
        }
    };

    return new PathLayer({
        id,
        data: segments,
        pickable: true,
        widthScale: widthScale, // 1 meter? No, path width is in meters by default usually, but let's check props.
        // PathLayer widthUnits: 'pixels' is default? No, 'meters' is default for most geospatial layers but PathLayer often defaults to 'meters'.
        // DeckGL v9: widthUnits default 'meters'.
        // Requirement: "3-4px". So sets widthUnits to 'pixels'.
        widthUnits: 'pixels',
        widthMinPixels: 3,
        widthMaxPixels: 4,
        getPath: d => d.path,
        getColor: d => getColor(d),
        getWidth: d => 4,
        capRounded: true,
        jointRounded: true,
        opacity: 1.0,
        visible,
        onHover, // Pass raw event for now, container handles tooltip conversion
        onClick: (info) => {
            if (onClick && info.object) {
                onClick({
                    object: info.object.startPoint, // Pass the data point
                    x: info.x,
                    y: info.y
                });
            }
        }
    });
};
